import StatusChip from 'components/@extended/StatusChip';
import { buttonStyle } from 'components/buttons/buttonStyle';
import { ExpandMore as ExpandMoreIcon } from 'components/icons';
import { Button } from 'components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from 'components/ui/collapsible';
import { useInvoiceMatterForm } from 'pages/invoices/hooks/useInvoiceMatterForm';
import { DetailsTabSuccessMessages } from 'pages/invoices/InvoicePage';
import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { ClassifierData } from 'types/classifiers';
import { FeatureFlag } from 'types/features';
import { InvoiceData, InvoiceMatterDetailsDto, InvoiceStatusType, InvoiceTransactionActionType } from 'types/invoices';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import { usePaymentCurrencyMode } from 'utils/usePaymentCurrencyMode';
import RecalculatingOverlay from '../../../components/RecalculatingOverlay';
import { ReasonTooltip } from '../../../components/ReasonTooltip';
import PaymentCurrencyNotice from '../../components/PaymentCurrencyNotice';
import { AlertActionButton, DetailsAlert } from '../Details';
import InvoiceExpenseTable from '../invoice-expenses/InvoiceExpenseTable';
import InvoiceTimecardTable from '../invoice-timecards/InvoiceTimecardTable';
import { SummaryHeaderLeftSide } from './component/SummaryHeaderLeftSide';
import { SummaryHeaderRightSide } from './component/SummaryHeaderRightSide';
import { SECTION_BODY, SECTION_CARD, SECTION_HEADER, SECTION_PANEL } from '../SectionCard';
import { InvoiceMatterNameDetails } from './InvoiceMatterNameDetails';
import { useCaseApproval } from './useCaseApproval';

interface Props {
    defaultExpanded: boolean;
    invoiceMatter: InvoiceMatterDetailsDto;
    invoice: InvoiceData;
    classifiers: Record<string, ClassifierData>;
    isInViewMode: boolean;
    addedInvoiceTimecardIds: number[];
    setAddedInvoiceTimecardIds: Dispatch<SetStateAction<number[]>>;
    setSuccessMessages: Dispatch<SetStateAction<DetailsTabSuccessMessages>>;
    successMessages: DetailsTabSuccessMessages;
    addedInvoiceExpenseIds: number[];
    setAddedInvoiceExpenseIds: Dispatch<SetStateAction<number[]>>;
    isLoading: boolean;
    isAddingNewTimecards: boolean;
    isAddingNewExpenses: boolean;
    isUpdatingTimecardsHourlyRates: boolean;
    handleStatusLoadingState: (isLoading: boolean) => void;
}

const InvoiceMatterDetails = ({
    defaultExpanded,
    invoiceMatter,
    invoice,
    classifiers,
    isInViewMode,
    addedInvoiceTimecardIds,
    setAddedInvoiceTimecardIds,
    setSuccessMessages,
    successMessages,
    addedInvoiceExpenseIds,
    setAddedInvoiceExpenseIds,
    isLoading,
    isAddingNewTimecards,
    isAddingNewExpenses,
    isUpdatingTimecardsHourlyRates,
    handleStatusLoadingState,
}: Props) => {
    const isLocalizedFieldsEnabled = useIsFeatureEnabled([
        FeatureFlag.LOCALIZED_FIELDS,
        FeatureFlag.LOCALIZED_FIELDS_INVOICES,
    ]);
    // NOTE: defaultExpanded is used only as the initial value; subsequent toggles
    // are user-driven via the expand icon, so this is a defaultProp
    // pattern, not a derived-state mirror.
    const [expanded, setExpanded] = useState<boolean>(defaultExpanded);

    const {
        form: { control, setValue, getValues, reset, handleSubmit },
        summaryData,
        initialData,
        isMatterSummaryLoading,
        isMatterSummaryFetching,
        handleStatusChange,
        handleUpdateFeeArrangementType,
        handleAdjustInvoiceMatter,
    } = useInvoiceMatterForm({ invoice, invoiceMatter, classifiers, handleStatusLoadingState });

    const {
        buttonsDisabled,
        openRejectModal,
        isInvoiceDraft,
        isInvoiceReviewed,
        isMatterStatusUpdatable,
        isInvoiceMatterApproved,
        isInvoiceMatterReviewed,
        isRejected,
        isMultiApprovalWorkflowActive,
        isInvoiceMatterReadOnly,
        isMatterFeeArrangementTypeUpdateable,
        hasMatterCapError,
        postponeReason,
    } = useCaseApproval({ invoice, invoiceMatter, summaryData, isInViewMode, handleStatusChange });
    const { isPaymentCurrencyMode, displayCurrency } = usePaymentCurrencyMode(invoice);

    const matterFeeArrangementTypeAlert = useMemo(
        () => (
            <DetailsAlert
                key="matterHourlyPriceChanged-warning"
                tone="warning"
                action={
                    <AlertActionButton
                        onClick={() => {
                            void handleUpdateFeeArrangementType();
                        }}
                        disabled={buttonsDisabled}
                    >
                        <FormattedMessage id="invoices.actions.update-invoice" />
                    </AlertActionButton>
                }
            >
                <FormattedMessage id="invoices.alerts.case-fee-arrangement-has-changed" />
            </DetailsAlert>
        ),
        [buttonsDisabled, handleUpdateFeeArrangementType]
    );

    const hasMatterFeeArrangementTypeChanged = summaryData?.hasMatterFeeArrangementTypeChanged;

    const alerts = useMemo<JSX.Element[]>(() => {
        if (!isMatterFeeArrangementTypeUpdateable || isInViewMode) {
            return [];
        }
        const result: JSX.Element[] = [];
        if (hasMatterFeeArrangementTypeChanged) {
            result.push(matterFeeArrangementTypeAlert);
        }
        return result;
    }, [
        isInViewMode,
        isMatterFeeArrangementTypeUpdateable,
        hasMatterFeeArrangementTypeChanged,
        matterFeeArrangementTypeAlert,
    ]);

    return (
        <Collapsible open={expanded} onOpenChange={setExpanded} className={SECTION_CARD}>
            <div className={SECTION_HEADER}>
                <div className="my-2.5 mr-4 ml-2 flex grow flex-col gap-4">
                    <div className="flex justify-between">
                        <InvoiceMatterNameDetails
                            invoice={invoice}
                            invoiceMatter={invoiceMatter}
                            isInViewMode={isInvoiceMatterReadOnly}
                            isLocalizedFieldsEnabled={isLocalizedFieldsEnabled}
                            isLoading={isMatterSummaryLoading}
                            classifiers={classifiers}
                        />
                        {isMultiApprovalWorkflowActive ? (
                            <div className="flex flex-row gap-4">
                                {(isInvoiceMatterReviewed && isInvoiceDraft) ||
                                (isInvoiceMatterApproved && isInvoiceReviewed) ? (
                                    <StatusChip
                                        label={
                                            <FormattedMessage
                                                id={`invoices.actions.${isInvoiceMatterApproved ? 'approved' : 'reviewed'}`}
                                            />
                                        }
                                        type={'success'}
                                    />
                                ) : (
                                    <ReasonTooltip reason={postponeReason}>
                                        <span className="inline-flex">
                                            <Button
                                                key="apporove"
                                                {...buttonStyle(
                                                    { variant: 'contained', color: 'secondary' },
                                                    'h-[30px] w-[100px]'
                                                )}
                                                disabled={
                                                    !isMatterStatusUpdatable || buttonsDisabled || hasMatterCapError
                                                }
                                                onClick={() => {
                                                    void handleStatusChange({
                                                        to: isInvoiceDraft
                                                            ? InvoiceStatusType.REVIEWED
                                                            : InvoiceStatusType.APPROVED,
                                                        transactionActionType: InvoiceTransactionActionType.APPROVED,
                                                    });
                                                }}
                                            >
                                                <FormattedMessage
                                                    id={`invoices.actions.${isInvoiceDraft ? 'to-REVIEWED' : 'to-APPROVED'}`}
                                                />
                                            </Button>
                                        </span>
                                    </ReasonTooltip>
                                )}
                                {isRejected ? (
                                    <StatusChip
                                        label={<FormattedMessage id={'invoices.actions.rejected'} />}
                                        type={'error'}
                                    />
                                ) : null}
                                {isInvoiceReviewed && !isInvoiceMatterApproved ? (
                                    <ReasonTooltip reason={postponeReason}>
                                        <span className="inline-flex">
                                            <Button
                                                key="reject"
                                                {...buttonStyle({ variant: 'outlined' }, 'h-[30px] w-[100px]')}
                                                disabled={!isMatterStatusUpdatable || buttonsDisabled}
                                                onClick={openRejectModal}
                                            >
                                                <FormattedMessage id="invoices.actions.reject" />
                                            </Button>
                                        </span>
                                    </ReasonTooltip>
                                ) : null}
                            </div>
                        ) : null}
                    </div>
                    {alerts}
                </div>
                <CollapsibleTrigger
                    aria-label={t('invoices.details.case', { fullName: invoiceMatter.fullName ?? '' })}
                    className="group focus-visible:ring-ring/50 flex cursor-pointer rounded-sm outline-none focus-visible:ring-3"
                >
                    <ExpandMoreIcon className="text-grey-500 transition-transform group-data-[panel-open]:rotate-90" />
                </CollapsibleTrigger>
            </div>
            {summaryData && (
                <CollapsibleContent keepMounted className={SECTION_PANEL}>
                    <div className={cn(SECTION_BODY, 'relative')}>
                        {isPaymentCurrencyMode && (
                            <PaymentCurrencyNotice currency={displayCurrency} className="mt-2 mb-4" />
                        )}
                        {/* Side-by-side only when the center pane (a CSS container) is wide enough;
                            stacks when the Summary drawer or a narrow window squeezes the pane. */}
                        <div className="grid grid-cols-1 @4xl:grid-cols-3">
                            <div className="content-start @4xl:pr-4">
                                <SummaryHeaderLeftSide
                                    control={control}
                                    summaryData={summaryData}
                                    invoiceId={invoice.id}
                                    invoiceMatter={invoiceMatter}
                                    isInViewMode={isInvoiceMatterReadOnly}
                                    onSubmit={handleAdjustInvoiceMatter}
                                    setValue={setValue}
                                    getValues={getValues}
                                    isPaymentCurrencyMode={isPaymentCurrencyMode}
                                    displayCurrency={displayCurrency}
                                />
                            </div>
                            <div className="border-divider mt-4 border-t pt-4 @4xl:col-span-2 @4xl:mt-0 @4xl:border-t-0 @4xl:border-l @4xl:pt-0 @4xl:pl-4">
                                <SummaryHeaderRightSide
                                    control={control}
                                    summaryData={summaryData}
                                    isInViewMode={isInvoiceMatterReadOnly}
                                    onSubmit={handleAdjustInvoiceMatter}
                                    setValue={setValue}
                                    getValues={getValues}
                                    isPaymentCurrencyMode={isPaymentCurrencyMode}
                                    displayCurrency={displayCurrency}
                                />
                            </div>
                        </div>
                        <div className="new:mt-(--card-gap) new:gap-(--card-gap) mt-6 flex flex-col gap-6">
                            <InvoiceTimecardTable
                                addedInvoiceTimecardIds={addedInvoiceTimecardIds}
                                setAddedInvoiceTimecardIds={setAddedInvoiceTimecardIds}
                                setSuccessMessages={setSuccessMessages}
                                successMessages={successMessages}
                                isInViewMode={isInvoiceMatterReadOnly}
                                invoice={invoice}
                                invoiceMatter={summaryData}
                                isLoading={
                                    isLoading || isAddingNewTimecards || isUpdatingTimecardsHourlyRates || !initialData
                                }
                                classifiers={classifiers}
                                validateForm={handleSubmit}
                                getValues={getValues}
                                setValue={setValue}
                                reset={reset}
                                control={control}
                            />
                            <InvoiceExpenseTable
                                addedInvoiceExpenseIds={addedInvoiceExpenseIds}
                                setAddedInvoiceExpenseIds={setAddedInvoiceExpenseIds}
                                setSuccessMessages={setSuccessMessages}
                                successMessages={successMessages}
                                isInViewMode={isInvoiceMatterReadOnly}
                                invoice={invoice}
                                invoiceMatter={summaryData}
                                isLoading={isLoading || isAddingNewExpenses || !initialData}
                                classifiers={classifiers}
                                validateForm={handleSubmit}
                                getValues={getValues}
                                setValue={setValue}
                                reset={reset}
                                control={control}
                            />
                        </div>
                        <RecalculatingOverlay
                            invoiceId={invoice.id}
                            isAlsoRecalculating={isMatterSummaryFetching}
                            hideLabel
                        />
                    </div>
                </CollapsibleContent>
            )}
        </Collapsible>
    );
};

export default InvoiceMatterDetails;
